import { Typography, Button, styled, Box, Divider } from '@mui/material';
import ClassHoldingsInfo, {
  type HoldingClassDetails,
} from './ClassHoldingsInfo';
import HoldingClassRightsForm from 'routes/LpPages/Common/HoldingClass/HoldingClassRightsForm';
import { Delete, Edit } from '@mui/icons-material';
import Row from 'components/Layout/Row';
import { useFormContext } from 'react-hook-form';
import api from 'lib/trpcClient';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import processAssetFullHistory from '../../helpers/processAssetFullHistory';
import AssetHistoryTable from '../../Revaluations/AssetHistoryTable';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AssetHistoryType } from 'further-types/lp/asset';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { Column } from 'further-ui/components/Layout';
import FullPageDrawer from 'components/new/FullPageDrawer';

const ClassRightsContainerColumn = styled(Column)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
}));

const ClassRightsDataContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.xs),
}));

const HorizontalPaddedColumn = styled(Column)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.sm),
}));

const CenteredFormContainer = styled(Box)({
  margin: 'auto',
  width: '100%',
  maxWidth: 400,
});

type ClassHoldingsListProps = {
  holdingClasses: Array<HoldingClassDetails>;
  onDeleteHoldingClass: (holdingClassId: string) => void;
  onUpdateHoldingClass: (holdingClassId: string) => Promise<boolean>;
  isUpdatingHoldingClass: boolean;
  companyId: string;
  vehicleId: string;
  vehicleCurrencyCode: CurrencyCode;
  editingHoldingClassId: string | undefined;
  onEditHoldingClass: (holdingClassId: string) => void;
  onCloseEditDrawer: () => void;
};

const ClassHoldingsList: React.FC<ClassHoldingsListProps> = ({
  holdingClasses,
  onDeleteHoldingClass,
  onUpdateHoldingClass,
  isUpdatingHoldingClass,
  companyId,
  vehicleId,
  vehicleCurrencyCode,
  editingHoldingClassId,
  onEditHoldingClass,
  onCloseEditDrawer,
}) => {
  const [canWriteHoldingClasses] = useHasPermissions([
    RolePermission.LpWriteHoldingClasses,
  ]);

  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const [holdingClassTotals] =
    api.asset.getAssetHoldingClassInvestmentTotals.useSuspenseQuery({
      assetId: companyId,
      vehicleId,
    });

  const [assetFullHistory] = api.asset.getAssetFullHistory.useSuspenseQuery({
    assetId: companyId,
    vehicleId,
  });

  const { control } = useFormContext();

  if (!holdingClasses) return null;

  const rows = holdingClasses.map((holdingClass) => ({
    id: holdingClass._id,
    ...holdingClass,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      label: 'Holding class',
      key: 'name',
      width: '150px',
      render: (holdingClass) => (
        <ColorIndicatorWithText
          text={holdingClass.name}
          colorKey={{ name: holdingClass.name }}
        />
      ),
    },
    {
      label: 'Units',
      key: 'currentNumberOfUnits',
      width: '150px',
      render: (holdingClass) =>
        numberToDisplayString(
          holdingClassTotals.find(
            (total) => total.holdingClassId === holdingClass._id,
          )?.currentNumberOfUnits ?? 0,
        ),
    },
    {
      label: 'Current value',
      key: 'currentHoldValue',
      width: '150px',
      render: (holdingClass) => {
        const total = holdingClassTotals.find(
          (total) => total.holdingClassId === holdingClass._id,
        );
        return numberToCurrencyString(total?.currentHoldValue ?? 0, {
          currency: total?.currency ?? vehicleCurrencyCode,
        });
      },
    },
    {
      label: 'Current unit price',
      key: 'currentUnitPrice',
      width: '150px',
      render: (holdingClass) => {
        const total = holdingClassTotals.find(
          (total) => total.holdingClassId === holdingClass._id,
        );
        return numberToCurrencyString(total?.latestUnitPrice ?? 0, {
          currency: total?.currency ?? vehicleCurrencyCode,
        });
      },
    },
    {
      label: 'Revaluations',
      key: 'revaluations',
      width: '150px',
      render: (holdingClass) =>
        numberToDisplayString(
          assetFullHistory[holdingClass._id]?.filter(
            (e) => e.type === AssetHistoryType.Revaluation,
          ).length ?? 0,
        ),
    },
  ];

  const editingIndex = holdingClasses.findIndex(
    (hc) => hc._id === editingHoldingClassId,
  );

  const handleEditSave = async () => {
    const success = await onUpdateHoldingClass(editingHoldingClassId!);
    if (success) onCloseEditDrawer();
  };

  return (
    <>
      <Table
        scrollable
        onRowExpand={({ id }) => toggleRowExpansion(id)}
        onRowClick={({ id }) => toggleRowExpansion(id)}
        isExpandableRow={() => true}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        getExpandedContent={(holdingClass) => {
          const holdingClassHistory = assetFullHistory[holdingClass._id] ?? [];

          const processedHistory = processAssetFullHistory({
            [holdingClass._id]: holdingClassHistory,
          });

          return (
            <Column spacing="sm">
              <ClassRightsContainerColumn spacing="md" fullWidth>
                <Divider />
                <HorizontalPaddedColumn fullWidth spacing="sm">
                  <Row centered fullWidth justify="space-between">
                    <Typography color="grey.600" variant="h6">
                      Class rights
                    </Typography>
                    {canWriteHoldingClasses && (
                      <Button
                        variant="outlined"
                        onClick={() => onEditHoldingClass(holdingClass._id)}
                        size="large"
                      >
                        Edit class rights
                      </Button>
                    )}
                  </Row>
                  <ClassRightsDataContainer>
                    <ClassHoldingsInfo holdingClass={holdingClass} />
                  </ClassRightsDataContainer>
                </HorizontalPaddedColumn>
                <Divider />
              </ClassRightsContainerColumn>
              {processedHistory.length > 0 && (
                <>
                  <HorizontalPaddedColumn fullWidth>
                    <Typography color="grey.600" variant="h6">
                      Class valuation history
                    </Typography>
                  </HorizontalPaddedColumn>
                  <AssetHistoryTable
                    assetHistory={processedHistory}
                    showHoldingClassName={false}
                    showSummary={false}
                  />
                </>
              )}
            </Column>
          );
        }}
        actions={[
          {
            icon: Delete,
            label: 'Delete class',
            hidden: !canWriteHoldingClasses,
            onClick: (holdingClass) => onDeleteHoldingClass(holdingClass._id),
          },
          {
            icon: Edit,
            label: 'Edit class rights',
            hidden: !canWriteHoldingClasses,
            onClick: (holdingClass) => onEditHoldingClass(holdingClass._id),
          },
        ]}
        columns={columns}
        rows={rows}
      />
      <FullPageDrawer
        open={editingHoldingClassId !== undefined}
        onClose={onCloseEditDrawer}
        title="Edit class rights"
        size="sm"
        innerScroll
        actions={
          <Row justify="space-between">
            <Button variant="outlined" size="large" onClick={onCloseEditDrawer}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleEditSave}
              disabled={isUpdatingHoldingClass}
            >
              Save changes
            </Button>
          </Row>
        }
      >
        {editingIndex !== -1 && (
          <CenteredFormContainer>
            <HoldingClassRightsForm
              key={editingHoldingClassId}
              index={editingIndex}
              formName="holdingClasses"
              control={control}
              vehicleCurrencyCode={vehicleCurrencyCode}
            />
          </CenteredFormContainer>
        )}
      </FullPageDrawer>
    </>
  );
};

export default ClassHoldingsList;
